import { z } from 'zod';
import { UuidSchema } from '../_shared/ids.js';
/**
 * El registro de lecturas del sitio público: la señal con la que se arma el bloque "Más leídas".
 *
 * **Por qué la cuenta el navegador y no el servidor.** El sitio está cacheado en tres capas —el CDN
 * (`s-maxage=60` + `stale-while-revalidate`), el caché de lecturas públicas de la API, y el HTML ya
 * renderizado que guarda el server SSR por URL—, así que un contador dentro del handler de la nota
 * contaría **misses de caché, no lectores**. Y como la nota más popular es justamente la que más
 * veces se sirve desde caché, el ranking saldría al revés. La única capa que se ejecuta una vez por
 * lector es el navegador: de ahí sale un `POST` que ninguna de las tres cachés toca.
 *
 * El pedido va al **mismo origen que la página** (el server SSR lo reenvía a la API con el `Host`
 * entrante) por tres razones concretas: la API resuelve el medio por `Host` y un pedido directo a
 * la API llevaría el host de la API; no hace falta CORS; y un path de primera parte que no dice
 * "ads", "track" ni "analytics" es el único que los bloqueadores no cancelan.
 */
/**
 * Cuánto tiene que estar la nota **a la vista** antes de contar la lectura.
 *
 * No es un número de estética: es lo que separa a un lector del rebote, del prefetch y del que
 * abrió y cerró. Un valor más chico cuenta más gente pero le da al ranking el mismo peso a los dos.
 */
export const LECTURA_DWELL_MS = 5_000;
/**
 * Cuánto vale una lectura del mismo lector sobre la misma nota. Recargar diez veces cuenta una:
 * si no, mover la tabla saldría gratis.
 *
 * También es el TTL de la marca de deduplicación, que es lo que la hace **efímera**: pasada esta
 * ventana no queda registro de que ese lector pasó por esa nota (ver `HUELLA_LECTOR_LARGO`).
 */
export const LECTURA_DEDUP_MINUTOS = 30;
/**
 * Largo en hexa de la huella con la que se deduplica un lector.
 *
 * La huella es `sha256(sal_del_día ‖ ip ‖ user-agent ‖ articleId)` **truncado**, y se usa como
 * *clave* de una marca que vence: nunca se guarda ni la IP ni el user-agent. La sal es aleatoria,
 * rota a diario y vive solo en memoria del acumulador, así que cuando vence no hay forma de atar
 * una huella vieja a una persona ni con la base en la mano. 16 bytes alcanzan de sobra para que dos
 * lectores distintos no colisionen.
 */
export const HUELLA_LECTOR_LARGO = 32;
/**
 * Cada cuánto se vuelca el acumulador caliente a la tabla de agregados.
 *
 * Las lecturas se acumulan por hora fuera de Postgres y se vuelcan cada tanto: una escritura por
 * visita sobre `articles` sería un tuple muerto por lector en la tabla más leída del sistema. Con
 * este período, la nota más leída del medio cuesta **una** fila por hora en vez de miles.
 */
export const LECTURA_VOLCADO_MS = 60_000;
/**
 * Cuánto se guardan los agregados por hora de `article_read_stats`.
 *
 * **Vive en el módulo de Audiencia y no acá.** Cuando esa tabla pasó a alimentar también la curva
 * "lecturas por hora" del detalle de una nota, su plazo dejó de ser una decisión de "Más leídas":
 * son los 90 días de `RETENCION_HORARIA_DIAS`. Tener dos constantes para la misma tabla es la
 * receta para que una de las dos quede vieja — y quedó: durante un tiempo esto decía 30 mientras el
 * tablero prometía 90, así que la curva de una nota se apagaba a los 30 sin que nada lo dijera.
 */
export { RETENCION_HORARIA_DIAS as LECTURA_RETENCION_DIAS } from './audiencia.js';
/**
 * La hora (UTC) a la que pertenece un instante, truncada. Es la granularidad de todo el registro.
 *
 * **Por hora y no por día ni por minuto.** Por día no se podría mover una ventana de 48 h sin
 * saltos de 24; por minuto serían 1.440 filas diarias por nota para una precisión que un ranking de
 * portada no usa. Y como un bucket cerrado ya no recibe escrituras, volcarlo no compite con nadie.
 */
export function horaDeLectura(instante) {
    const hora = new Date(instante);
    hora.setUTCMinutes(0, 0, 0);
    return hora;
}
/** Clave del bucket de esa hora (`2026081214`), que es como se nombra en el acumulador. */
export function claveDeHora(instante) {
    return horaDeLectura(instante).toISOString().slice(0, 13).replace(/[-T]/g, '');
}
/** La hora más vieja que entra en una ventana de `horas` contada desde `ahora`. */
export function desdeLaVentana(ahora, horas) {
    return horaDeLectura(new Date(ahora.getTime() - horas * 60 * 60 * 1_000));
}
/**
 * Lo único que viaja en el aviso de lectura: qué nota se leyó.
 *
 * **No lleva cuerpo ni datos del lector.** Quién es lo resuelve el servidor con lo que ya tiene del
 * pedido (y solo para deduplicar, en forma de huella que vence), y el medio sale del `Host`. Un
 * campo más acá sería un dato de una persona viajando por la red sin necesidad.
 */
export const RegistrarLecturaParams = z.object({ articleId: UuidSchema }).strict();
